import { AnimatePresence, motion } from 'framer-motion'
import { CheckCircle, AlertTriangle, XCircle, Info, X } from 'lucide-react'
import { useToastStore } from '@/stores/toastStore'
import type { ToastVariant } from '@/types'

const icons: Record<ToastVariant, React.ReactNode> = {
  success: <CheckCircle size={16} className="text-[#22C55E] shrink-0" />,
  warning: <AlertTriangle size={16} className="text-[#EAB308] shrink-0" />,
  error: <XCircle size={16} className="text-[#EF4444] shrink-0" />,
  info: <Info size={16} className="text-[#3B82F6] shrink-0" />,
}

const borderColor: Record<ToastVariant, string> = {
  success: 'border-l-[#22C55E]',
  warning: 'border-l-[#EAB308]',
  error: 'border-l-[#EF4444]',
  info: 'border-l-[#3B82F6]',
}

export function Toaster() {
  const { toasts, remove } = useToastStore()

  return (
    <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 pointer-events-none">
      <AnimatePresence mode="popLayout">
        {toasts.map((t) => (
          <motion.div
            key={t.id}
            layout
            initial={{ opacity: 0, x: 48, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 48, scale: 0.9 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            className={[
              'pointer-events-auto w-80 max-w-[calc(100vw-2rem)]',
              'bg-[#141414] border border-[#1E1E1E] border-l-4 rounded-xl',
              'shadow-[0_8px_32px_rgba(0,0,0,0.5)]',
              'px-4 py-3 flex items-start gap-3',
              borderColor[t.variant],
            ].join(' ')}
          >
            <span className="mt-0.5">{icons[t.variant]}</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#F5F5F5] leading-snug">{t.title}</p>
              {t.description && (
                <p className="text-xs text-[#A0A0A0] mt-0.5 leading-relaxed">{t.description}</p>
              )}
            </div>
            <button
              onClick={() => remove(t.id)}
              className="text-[#6B6B6B] hover:text-[#F5F5F5] transition-colors mt-0.5 shrink-0"
              aria-label="Dismiss"
            >
              <X size={14} />
            </button>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  )
}
